<template>
  <Head title="Evanota — Notas justas y claras" />

  <PublicLayout>
    <!-- Benefits Section -->
    <section class="md:pt-20">
      <div class="container mx-auto px-4">
        <div class="text-center m-16 flex flex-col items-center gap-8">
          <h2 class="text-5xl md:text-7xl font-bold max-w-3xl">
            Notas justas y claras,<br>sin perder tus tardes.
          </h2>
          <p class="text-xl max-w-2xl">
            Evanota calcula las notas de tu alumnado con los criterios oficiales de la LOMLOE y te enseña de dónde sale cada una. Únete a los docentes que están cambiando cómo se evalúa.
          </p>
          <div class="flex flex-col items-center gap-4">
            <Link href="/plan">
              <Button
                class="cursor-pointer px-8 py-6 text-lg"
                size="lg"
              >
                Empieza gratis 4 meses
              </Button>
            </Link>
            <p class="text-sm text-muted-foreground">
              Sin tarjeta · Después, 2,99 € al mes
            </p>
          </div>
        </div>

        <div class="mx-auto w-full max-w-5xl">
          <Carousel
            class="relative w-full"
            aria-label="Capturas de pantalla de Evanota"
            :opts="{ loop: true, align: 'start' }"
            @init-api="setCarouselApi"
          >
            <div class="overflow-hidden rounded-2xl border border-border bg-muted">
              <CarouselContent>
                <CarouselItem
                  v-for="(slide, index) in slides"
                  :key="slide.alt"
                >
                  <img
                    :src="slide.src"
                    :alt="slide.alt"
                    class="aspect-[3/2] w-full object-cover object-top"
                    :loading="index === 0 ? 'eager' : 'lazy'"
                    :fetchpriority="index === 0 ? 'high' : undefined"
                  >
                </CarouselItem>
              </CarouselContent>
            </div>

            <CarouselPrevious class="left-2 md:-left-12">
              <ArrowLeft class="h-4 w-4 text-current" />
              <span class="sr-only">Diapositiva anterior</span>
            </CarouselPrevious>
            <CarouselNext class="right-2 md:-right-12">
              <ArrowRight class="h-4 w-4 text-current" />
              <span class="sr-only">Diapositiva siguiente</span>
            </CarouselNext>
          </Carousel>

          <div class="mt-4 flex justify-center gap-2">
            <button
              v-for="(slide, index) in slides"
              :key="slide.alt"
              type="button"
              class="size-2.5 rounded-full transition-colors"
              :class="currentSlide === index ? 'bg-primary' : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'"
              :aria-label="`Ir a captura: ${slide.alt}`"
              :aria-current="currentSlide === index ? 'true' : undefined"
              @click="scrollToSlide(index)"
            />
          </div>
        </div>

        <div class="grid md:grid-cols-3 gap-8 m-16 pt-16">
          <!-- Benefit 1 -->
          <Card class="text-left hover:shadow-lg transition-shadow">
            <CardHeader>
              <h3 class="text-2xl font-semibold">
                Fácil de usar
              </h3>
            </CardHeader>
            <CardContent>
              <p class="">
                Da un peso a cada criterio y Evanota hace las cuentas. Ves paso a paso cómo sale cada nota, para explicarla con seguridad a familias y alumnado.
              </p>
            </CardContent>
          </Card>

          <!-- Benefit 2 -->
          <Card class="text-left hover:shadow-lg transition-shadow">
            <CardHeader>
              <h3 class="text-2xl font-semibold mb-2">
                Criterios Oficiales
              </h3>
            </CardHeader>
            <CardContent>
              <p class="">
                Importa criterios de evaluación oficiales para las materias según la LOMLOE de España. Incluye todos los
                criterios oficiales para cada asignatura.
              </p>
            </CardContent>
          </Card>

          <!-- Benefit 3 -->
          <Card class="text-left hover:shadow-lg transition-shadow">
            <CardHeader>
              <h3 class="text-2xl font-semibold mb-2">
                Seguro y Confiable
              </h3>
            </CardHeader>
            <CardContent>
              <p class="">
                Los datos de tu alumnado, protegidos y siempre disponibles. Cumplimos el RGPD y guardamos todo en servidores de la UE.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>


    <!-- Beliefs Section -->
    <section class="py-16 md:py-20">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16 flex flex-col items-center gap-4">
          <h2 class="text-4xl md:text-5xl font-bold">
            En lo que creemos
          </h2>
          <p class="text-lg text-muted-foreground max-w-2xl">
            Evanota es una herramienta, pero también una forma de entender la evaluación. Si te reconoces aquí, este es tu sitio.
          </p>
        </div>

        <div class="grid md:grid-cols-3 gap-12 md:gap-8">
          <div class="text-left flex flex-col gap-3">
            <span class="text-sm text-muted-foreground/60 tracking-wide">01</span>
            <h3 class="text-xl font-semibold">
              Toda nota se puede explicar
            </h3>
            <p class="text-muted-foreground">
              Un alumno merece saber por qué tiene un 6. Una familia también. Evaluar con criterios claros es evaluar con justicia.
            </p>
          </div>

          <div class="text-left flex flex-col gap-3">
            <span class="text-sm text-muted-foreground/60 tracking-wide">02</span>
            <h3 class="text-xl font-semibold">
              Tu tiempo es para enseñar
            </h3>
            <p class="text-muted-foreground">
              Las hojas de cálculo eternas no hacen mejor docente a nadie. Las cuentas, para la máquina; el aula, para ti.
            </p>
          </div>

          <div class="text-left flex flex-col gap-3">
            <span class="text-sm text-muted-foreground/60 tracking-wide">03</span>
            <h3 class="text-xl font-semibold">
              Mejoramos juntos
            </h3>
            <p class="text-muted-foreground">
              Evanota crece con lo que nos cuentan quienes la usan cada día. Cada docente que se une hace la herramienta mejor para el siguiente.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Contact Section -->
    <section class="py-0">
      <div class="container mx-auto px-4">
        <div class="max-w-4xl mx-auto">
          <Card class="max-w-2xl mx-auto text-center">
            <CardHeader>
              <h3 class="text-2xl font-semibold">
                ¿Te unes?
              </h3>
            </CardHeader>
            <CardContent class="space-y-6">
              <p class="text-muted-foreground">
                4 meses gratis, sin tarjeta. Después, 2,99 € al mes.
              </p>
              <div class="flex flex-wrap justify-center gap-3">
                <Link href="/plan">
                  <Button
                    class="cursor-pointer"
                    size="lg"
                  >
                    Si, quiero
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  </PublicLayout>
</template>

<script setup>
import { Head, Link, usePage } from '@inertiajs/vue3';
import { watchOnce } from '@vueuse/core';
import { ArrowLeft, ArrowRight } from 'lucide-vue-next';
import { onMounted, ref } from 'vue';
import Button from '@/components/ui/button/Button.vue';
import Card from '@/components/ui/card/Card.vue';
import CardHeader from '@/components/ui/card/CardHeader.vue';
import CardContent from '@/components/ui/card/CardContent.vue';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import PublicLayout from '@/layouts/PublicLayout.vue';

import dashboardImg from '../../../images/demo/Dashboard-EvaNota.jpg';
import studentsImg from '../../../images/demo/Students-EvaNota.jpg';
import asignaturasImg from '../../../images/demo/Asignaturas-EvaNota.jpg';
import competenciasImg from '../../../images/demo/Competencias-EvaNota.jpg';
import periodosImg from '../../../images/demo/Periodos-EvaNota.jpg';
import usuariosImg from '../../../images/demo/Usuarios-EvaNota.jpg';

const page = usePage();

onMounted(() => {
  console.log(`Evanota version: ${page.props.version}`);
});

const slides = [
  { src: dashboardImg, alt: 'Dashboard' },
  { src: studentsImg, alt: 'Alumnado' },
  { src: asignaturasImg, alt: 'Asignaturas' },
  { src: competenciasImg, alt: 'Competencias' },
  { src: periodosImg, alt: 'Periodos' },
  { src: usuariosImg, alt: 'Usuarios' },
];

const carouselApi = ref(null);
const currentSlide = ref(0);

function setCarouselApi(api) {
  carouselApi.value = api;
}

function scrollToSlide(index) {
  carouselApi.value?.scrollTo(index);
}

watchOnce(carouselApi, (api) => {
  if (!api) {
    return;
  }

  const syncCurrentSlide = () => {
    currentSlide.value = api.selectedScrollSnap();
  };

  syncCurrentSlide();

  api.on('select', syncCurrentSlide);
  api.on('reInit', syncCurrentSlide);
});
</script>
